import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchScheduledReports,
  createScheduledReport,
  updateScheduledReport,
  deleteScheduledReport,
  runScheduledReport,
  runDataExtract,
  fetchRevenueStatistics,
  fetchRoomSoldStatistics,
  type CreateScheduledReportPayload,
  type UpdateScheduledReportPayload,
  type DataExtractQuery,
  type StatisticsQuery,
} from '@/services/api/reportsDepth.api';

// ─── Scheduler ────────────────────────────────────────────────────────────────

export function useScheduledReports(propertyId?: string, enabled = true) {
  return useQuery({
    queryKey: ['reports', 'schedules', propertyId],
    queryFn: () => fetchScheduledReports(propertyId),
    staleTime: 30_000,
    enabled,
  });
}

export function useCreateScheduledReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateScheduledReportPayload) =>
      createScheduledReport(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reports', 'schedules'] });
    },
  });
}

export function useUpdateScheduledReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateScheduledReportPayload;
    }) => updateScheduledReport(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reports', 'schedules'] });
    },
  });
}

export function useDeleteScheduledReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteScheduledReport(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reports', 'schedules'] });
    },
  });
}

export function useRunScheduledReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => runScheduledReport(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reports', 'schedules'] });
    },
  });
}

// ─── Data Extracts ────────────────────────────────────────────────────────────

export function useRunDataExtract() {
  return useMutation({
    mutationFn: (query: DataExtractQuery) => runDataExtract(query),
  });
}

// ─── Statistics ──────────────────────────────────────────────────────────────

export function useRevenueStatistics(query: StatisticsQuery, enabled = true) {
  return useQuery({
    queryKey: ['reports', 'statistics', 'revenue', query],
    queryFn: () => fetchRevenueStatistics(query),
    staleTime: 30_000,
    enabled: enabled && !!query.propertyId,
  });
}

export function useRoomSoldStatistics(query: StatisticsQuery, enabled = true) {
  return useQuery({
    queryKey: ['reports', 'statistics', 'rooms-sold', query],
    queryFn: () => fetchRoomSoldStatistics(query),
    staleTime: 30_000,
    enabled: enabled && !!query.propertyId,
  });
}
